import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { IMAGES } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genSession = () => "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSession);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey — I'm the LightSpeed ROI assistant. Ask me anything about getting a free website and booking more jobs. What trade are you in?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — connection hiccup. Call or text Dyan at 832-280-5553." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-[60] flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-transform hover:scale-105"
        style={{ backgroundColor: "var(--ls-orange)" }}
        aria-label="Open chat"
      >
        {open ? <X className="w-6 h-6" stroke="#0a0f16" /> : <MessageCircle className="w-6 h-6" stroke="#0a0f16" fill="#0a0f16" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-[380px] rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          style={{ background: "var(--ls-surface)", border: "1px solid var(--ls-border-strong)", height: "min(560px, 72vh)" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4" style={{ background: "#05070b", borderBottom: "1px solid var(--ls-border)" }}>
            <img src={IMAGES.logo} alt="LightSpeed ROI" className="h-9 w-9 rounded-md object-contain bg-black" />
            <div>
              <p className="font-cond font-bold text-white uppercase tracking-wide leading-none">LightSpeed AI</p>
              <p className="text-xs text-[#9a8f80] mt-1">Typically replies instantly</p>
            </div>
            <span className="ml-auto flex items-center gap-1.5 text-xs text-[#b7ab9b]">
              <span className="w-2 h-2 rounded-full bg-green-500" /> Online
            </span>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-[15px] leading-snug ${
                    m.role === "user"
                      ? "text-[#0a0f16] rounded-2xl rounded-br-md"
                      : "text-[#ece5da] rounded-2xl rounded-bl-md"
                  }`}
                  style={
                    m.role === "user"
                      ? { backgroundColor: "var(--ls-orange)" }
                      : { background: "#05070b", border: "1px solid var(--ls-border)" }
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-2.5 rounded-2xl rounded-bl-md" style={{ background: "#05070b", border: "1px solid var(--ls-border)" }}>
                  <Loader2 className="w-4 h-4 animate-spin text-orange" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="p-3 flex items-center gap-2" style={{ borderTop: "1px solid var(--ls-border)" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 px-4 py-2.5 rounded-full text-white placeholder:text-[#7d7264] outline-none text-sm"
              style={{ background: "#05070b", border: "1px solid var(--ls-border)" }}
            />
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center w-10 h-10 rounded-full shrink-0"
              style={{ backgroundColor: "var(--ls-orange)" }}
              aria-label="Send"
            >
              <Send className="w-4 h-4" stroke="#0a0f16" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
